
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PuttSituation } from "@/types/golf";
import FavoriteButton from "@/components/common/FavoriteButton";
import { useTranslation } from "react-i18next";

interface ApproachCardProps {
  approach: PuttSituation;
  index: number;
  onSelectTest: (situation: PuttSituation) => void;
  refreshAfterToast?: () => void;
}

const ApproachCard = ({ approach, index, onSelectTest, refreshAfterToast }: ApproachCardProps) => {
  const { t } = useTranslation('training');
  
  // Map approach IDs to translation keys
  const translationKeyMap: Record<number, string> = {
    7: "liftedApproaches",
    8: "longChips",
    9: "shortChips",
    10: "wedging50m",
    11: "wedging30m",
    12: "shortBunker",
    13: "longBunker",
    14: "midDistanceApproaches",
    15: "roughApproaches",
  };
  
  const exerciseKey = translationKeyMap[approach.id];
  const name = exerciseKey ? t(`approaches.exercises.${exerciseKey}.name`) : approach.name;
  const description = exerciseKey ? t(`approaches.exercises.${exerciseKey}.description`) : approach.description;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Card className="h-full hover:shadow-lg transition-shadow duration-300">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-golf-700">{name}</CardTitle>
            <FavoriteButton 
              testId={approach.id.toString()} 
              testType="approach"
              refreshAfterToast={refreshAfterToast}
            />
          </div>
          <CardDescription className="text-center">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <img
            src={approach.image}
            alt={name}
            className="w-full h-48 object-cover rounded-md mb-4"
          />
          <Button
            onClick={() => onSelectTest(approach)}
            className="w-full bg-golf-600 hover:bg-golf-700 text-white"
          >
            {t('approaches.startTest')}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ApproachCard;
